import { Table } from 'antd';
import classNames from 'classnames';
import EmptyState from 'components/ui/EmptyState';
import { useAppContext } from 'context/state';
import { useTariffContext } from 'context/tariff_state';
import { OrderStatus } from 'data/interfaces/Common';
import { addDays, compareDesc, format, parse, parseISO } from 'date-fns';
import { useLocale, useTranslations } from 'shared/hooks/i18n';
import Image from 'next/image';
import { FC } from 'react';
import { InterfaceLanguage, MonetizationTariff, ProductType } from 'types/enums';
import { getCurrencySymbol } from 'utils/commonObjects';
import { getCountedItemKey } from 'utils/getCountedItemKey';
import { getLocaleFromLang } from 'utils/getLocaleFromLang';
import { ShopPages } from '..';
import Badge from '../shared/Badge';
import styles from './style.module.scss';

interface TableData {
  tariff: MonetizationTariff;
  price: number;
  dateOfBuy: string;
  expiredIn: string;
  isActive: boolean;
  currency: string;
  countMax: number;
  type: ProductType;
}

interface Props {
  setPage: (n: ShopPages) => void;
}

const History: FC<Props> = ({ setPage }) => {
  const t = useTranslations();
  const lang = useLocale();
  const appContext = useAppContext();
  const tariffContext = useTariffContext();

  const orders = tariffContext.orders
    .reduce((acc, cur) => {
      const item = {
        tariff: cur.contents.tariff,
        price: cur.contents.price,
        dateOfBuy: cur.createdAt
          ? format(parseISO(cur.createdAt), 'dd.MM.yyyy', {
              locale: getLocaleFromLang(lang as InterfaceLanguage),
            })
          : '',
        expiredIn: format(
          addDays(parseISO(cur.createdAt), cur.contents.productValidityPeriod),
          'dd.MM.yyyy',
          {
            locale: getLocaleFromLang(lang as InterfaceLanguage),
          },
        ),
        isActive: cur.status === OrderStatus.PAID || cur.status === OrderStatus.USE,
        currency: cur.contents.meta.currencySymbol,
        countMax: cur.contents.totalCount,
        type: cur.contents.type,
      };

      acc.push(item);

      return acc;
    }, [] as TableData[])
    .sort((a, b) =>
      compareDesc(
        parse(a.dateOfBuy, 'dd.MM.yyyy', new Date()),
        parse(b.dateOfBuy, 'dd.MM.yyyy', new Date()),
      ),
    );

  const columns = [
    {
      title: t('tariff'),
      dataIndex: 'tariff',
      key: 'tariff',
      render: (text: string, record: any) => (
        <div className={styles.tariff}>
          <span className={styles.name}>{t(text)}</span>
          <span className={styles.placements}>
            {t(getCountedItemKey(record.type), { count: record.countMax })}
          </span>
        </div>
      ),
    },
    {
      title: t('price'),
      dataIndex: 'price',
      key: 'price',
      render: (value: number, record: any) => (
        <span>
          {getCurrencySymbol(record.currency)}
          {value}
        </span>
      ),
    },
    {
      title: t('date_of_buy'),
      dataIndex: 'dateOfBuy',
      key: 'dateOfBuy',
    },
    {
      title: t('expired_in'),
      dataIndex: 'expiredIn',
      key: 'expiredIn',
    },
    {
      title: t('managers_status'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value: boolean, record: any) => <Badge isActive={value} tariff={record.tariff} />,
    },
  ];

  const handleClickEmpty = () => {
    setPage(ShopPages.AllOffers);
  };

  if (!orders.length) {
    return (
      <EmptyState
        description={t('purchased_tariff_will_be_visible_in_this_section')}
        buttonText={t('set')}
        onClick={handleClickEmpty}
        icon={<Image src='/icons/empty-circle.svg' width={100} height={100} alt='empty circle' />}
      />
    );
  }

  return (
    <div className={styles.container}>
      {appContext.size.isTabletWidth ? (
        orders.map((item, idx, arr) => (
          <div key={JSON.stringify(item)}>
            <span className={styles.cardTitleDate}>{item.dateOfBuy}</span>
            <div
              className={classNames(styles.card, {
                [styles.card__last]: idx === arr.length - 1,
              })}>
              <div className={styles.cardContent}>
                <span
                  className={
                    styles.cardName
                  }>{`${t(item.tariff)} - ${t(getCountedItemKey(item.type), { count: 3 })}`}</span>
                <span className={styles.price}>
                  {item.currency}
                  {item.price}
                </span>
              </div>
              <div className={styles.cardContent}>
                <Badge isActive={item.isActive} tariff={item.tariff} />
                <span className={styles.cardExpirationDate}>{`${t('to')} ${item.expiredIn}`}</span>
              </div>
            </div>
          </div>
        ))
      ) : (
        <Table
          rowClassName={() => styles.tableRow}
          dataSource={orders}
          columns={columns}
          className={styles.table}
          pagination={false}
          bordered={false}
        />
      )}
    </div>
  );
};

export default History;
